/**
 * FleetHealth — Phase 2 real implementation.
 *
 * Polls GET /api/fleet/health every 10s. Shows stat cards + per-agent table.
 * NEVER fabricates data — missing fields display "—".
 *
 * The api client now reads the bearer token from tokenManager and handles
 * silent refresh transparently. An UnauthorizedError reaching this component
 * means the refresh also failed (console session gone); tokenManager will have
 * already called onExpired to transition the App to unauthenticated.
 */
import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api, UnauthorizedError } from '../api/client'
import type { FleetHealth as FleetHealthData, AgentStatus } from '../api/client'
import { relativeTime } from '../utils/relativeTime'
import { useCoreStatus } from '../context/CoreStatusContext'
import { usePendingChangeSignal } from '../hooks/usePendingChangeSignal'

const POLL_INTERVAL_MS = 10_000

function fmt(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined) return '—'
  return n.toFixed(decimals)
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${(n * 100).toFixed(1)}%`
}

function outcomeBadge(outcome: string | null): React.ReactNode {
  if (!outcome) return <span className="text-gray-400">—</span>
  const map: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    error:   'bg-red-100 text-red-800',
    timeout: 'bg-amber-100 text-amber-800',
    empty:   'bg-gray-100 text-gray-600',
  }
  const cls = map[outcome] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {outcome}
    </span>
  )
}

interface StatCardProps {
  label: string
  value: string
  sub?: string
  href?: string
  warn?: boolean
}

function StatCard({ label, value, sub, href, warn }: StatCardProps) {
  const body = (
    <div className={`rounded-lg border bg-white p-5 ${warn ? 'border-amber-300' : 'border-gray-200'}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
  if (href) {
    return <Link to={href}>{body}</Link>
  }
  return body
}

function AgentRow({ agent }: { agent: AgentStatus }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-mono text-gray-800">{agent.profile}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{relativeTime(agent.last_seen)}</td>
      <td className="px-4 py-3 text-sm">{outcomeBadge(agent.last_outcome)}</td>
      <td className="px-4 py-3 text-sm text-gray-700 text-right">{agent.calls_24h}</td>
      <td className="px-4 py-3 text-sm text-right">
        {agent.failures_24h > 0
          ? <span className="text-red-600 font-medium">{agent.failures_24h}</span>
          : <span className="text-gray-400">0</span>}
      </td>
    </tr>
  )
}

function fmtGB(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return '—'
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`
}

export function FleetHealth() {
  // ADR-099 D8 — the shared core-status poll, for the slim "Core update"
  // pointer below (the full accept/apply flow now lives on the Updates page).
  const { status: coreStatus } = useCoreStatus()
  // ADR-122 D8b — Health points to Updates for either operator Changes or a
  // ready Platform update, where both can be taken live.
  const { waiting: changesWaiting, platformSyncPending } = usePendingChangeSignal()

  const [data, setData] = useState<FleetHealthData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [secsAgo, setSecsAgo] = useState(0)

  const fetchHealth = useCallback(async () => {
    try {
      const health = await api.getFleetHealth()
      setData(health)
      setLastUpdated(new Date())
      setSecsAgo(0)
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError('Session expired. Please re-authenticate.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load fleet health.')
      }
    }
  }, [])

  // Initial fetch + poll every 10s.
  useEffect(() => {
    void fetchHealth()
    const interval = setInterval(() => { void fetchHealth() }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchHealth])

  // Tick the "updated Xs ago" counter each second.
  useEffect(() => {
    if (!lastUpdated) return
    const t = setInterval(() => {
      setSecsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
    }, 1000)
    return () => clearInterval(t)
  }, [lastUpdated])

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Fleet Health</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time health and delegation metrics.</p>
        </div>
        {lastUpdated && (
          <p className="text-xs text-gray-400">
            Updated {secsAgo}s ago
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ADR-099 D8 — a slim pointer to the Updates page, which is now the ONE
          obvious home for the status + changelog + accept/apply action (was a
          full card here under P3.3; folded per the D8 addendum).

          ADR-122 D8b: the shared nav signal covers operator Changes and a ready
          Platform update, and this card sends either case to the same apply
          path on Updates. */}
      <Link
        to="/updates"
        className="block rounded-lg border border-gray-200 bg-white p-4 hover:border-indigo-300 transition-colors"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            {coreStatus?.available === true ? (
              coreStatus.update_available ? (
                <span className="text-indigo-700 font-medium">
                  <span aria-hidden="true">⬆</span> Core update available — {coreStatus.commits_behind} commit
                  {coreStatus.commits_behind === 1 ? '' : 's'} behind
                </span>
              ) : (
                <span className="text-gray-600">
                  <span aria-hidden="true">✓</span> Core up to date
                </span>
              )
            ) : (
              <span className="text-gray-400">Core update status unavailable</span>
            )}
          </div>
          <span className="text-xs text-indigo-600 shrink-0">Manage updates →</span>
        </div>
        {(changesWaiting || platformSyncPending) && (
          <div className="mt-2 pt-2 border-t border-gray-100 space-y-1 text-xs">
            {changesWaiting && (
              <p className="text-amber-700">Changes are waiting to go live in this fleet.</p>
            )}
            {platformSyncPending && (
              <p className="text-amber-700">A platform update is ready. Take it live from Updates.</p>
            )}
          </div>
        )}
      </Link>

      {!data && !error && (
        <div className="text-sm text-gray-400 animate-pulse">Loading health data...</div>
      )}

      {data && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <section className="border rounded p-3">
              <h2>Slack serialization</h2>
              {data.slack_serial?.status==='unreadable'||!data.slack_serial
                ? <p>{data.slack_serial?.reason||'Serialization metrics unreadable'}</p>
                : <dl><dt>Eligible</dt><dd>{fmt(data.slack_serial.serial_eligible)}</dd>
                  <dt>Contention</dt><dd>{fmt(data.slack_serial.serial_contention)}</dd>
                  <dt>Wait duration</dt><dd>{fmt(data.slack_serial.serial_wait_seconds,1)} s</dd>
                  <dt>Timeouts</dt><dd>{fmt(data.slack_serial.serial_timeout)}</dd></dl>}
            </section>
            <StatCard
              label="Delegation success (24h)"
              value={fmtPct(data.delegation_24h.success_rate)}
              sub={`${data.delegation_24h.total} calls total`}
            />
            <StatCard
              label="Timed out / errors (24h)"
              value={`${data.delegation_24h.timeout} / ${data.delegation_24h.error}`}
              warn={data.delegation_24h.timeout + data.delegation_24h.error > 0}
              sub={`avg ${fmt(data.delegation_24h.avg_duration_s, 1)}s`}
            />
            <StatCard
              label="Pending approvals"
              value={String(data.approvals.pending)}
              href="/approvals"
              warn={data.approvals.pending > 0}
              sub={data.approvals.oldest_pending_min !== null
                ? `oldest ${fmt(data.approvals.oldest_pending_min, 0)}m ago`
                : undefined}
            />
            <StatCard
              label="Avg grade (24h)"
              value={data.grades_24h.count > 0 ? fmt(data.grades_24h.avg, 1) : '—'}
              sub={data.grades_24h.count > 0 ? `${data.grades_24h.count} graded` : 'no grades yet'}
            />
            {data.feedback?.reaction_grading && (
              <StatCard
                label="👍 Reaction feedback (24h)"
                value={data.feedback.reaction_grading.ready
                  ? String(data.feedback.reaction_grading.reactions_24h)
                  : 'off'}
                warn={!data.feedback.reaction_grading.ready}
                sub={data.feedback.reaction_grading.ready
                  ? (data.feedback.reaction_grading.reactions_24h > 0 ? 'reactions today' : 'ready — none yet')
                  : 'FLEET_ID not set — fix in deploy config'}
              />
            )}
            <section className="rounded border p-4">
              <h2>Kanban storage</h2>
              {!data.kanban_storage || data.kanban_storage.status === 'unreadable'
                ? <p>{data.kanban_storage?.reason || 'Hermes volume unreadable'}</p>
                : <><p>{fmtGB(data.kanban_storage.db_bytes)} database</p>
                  <p>{fmtGB(data.kanban_storage.free_bytes)} free</p>
                  <p>Threshold: {fmtGB(data.kanban_storage.threshold_free_bytes)}</p>
                  {data.kanban_storage.attention && <p role="alert">Low space needs attention</p>}</>}
            </section>
            {data.execution_pause && (data.execution_pause.state === 'unreadable' || data.execution_pause.paused) && (
              <section className="rounded border p-4">
                {data.execution_pause.state === 'unreadable'
                  ? <p>Console unreachable since {data.execution_pause.console_unreachable_since || 'unknown'}</p>
                  : <>
                    <h2>Automatic execution paused</h2>
                    <p>{data.execution_pause.set_by || 'No operator record'}</p>
                    <p>{data.execution_pause.set_at}</p>
                    <p>{data.execution_pause.reason}</p>
                    {data.execution_pause.test_resume && <>
                      <p>Test resume: {data.execution_pause.test_resume.producer}</p>
                      <p>{data.execution_pause.test_resume.until}</p>
                      <p>{data.execution_pause.test_resume.set_by}</p>
                      <p>{data.execution_pause.test_resume.set_at}</p>
                      <p>{data.execution_pause.test_resume.reason}</p>
                    </>}
                  </>}
              </section>
            )}
            {data.restored_schema && (
              <section className="rounded border p-4">
                <h2>Restored Hermes schema</h2>
                {data.restored_schema.status === 'unreadable'
                  ? <><p>Unreadable</p><p>{data.restored_schema.reason}</p></>
                  : <><p>Schema version: {data.restored_schema.schema_version}</p>
                    <p>Archive: {data.restored_schema.archive_id}</p>
                    <p>Restored: {data.restored_schema.restored_at}</p></>}
              </section>
            )}
            {data.storage && (
              <StatCard
                label="Storage"
                value={data.storage.error ? 'unknown' : `${fmt(data.storage.used_percent ?? null, 0)}% full`}
                warn={Boolean(data.storage.warn) || Boolean(data.storage.error)}
                sub={(data.storage.error
                  ? 'could not read disk usage'
                  : `${fmtGB(data.storage.free_bytes)} free of ${fmtGB(data.storage.total_bytes)}`
                    + (data.storage.warn ? ` — over ${data.storage.warn_percent}%, clear out old projects` : ''))
                  + (data.storage.chat_alert && !data.storage.chat_alert.armed ? ' · chat warning not set up'
                    : data.storage.chat_alert?.last_error ? ' · last chat warning failed to post' : '')
                  + (data.run_workspace_sweep === 'off' ? ' · run files kept (no 48h sweep)' : '')}
              />
            )}
          </div>

          {/* Per-agent table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Agents</h2>
            </div>
            {data.agents.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-400 text-center">No agent activity in the last 24h.</p>
            ) : (
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Profile</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last seen</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last outcome</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Calls (24h)</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Failures</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.agents.map(agent => (
                    <AgentRow key={agent.profile} agent={agent} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}
